import {
  claimHistoriesSchema,
  type ClaimHistoryActivity,
} from '@billing/validation/claimHistory';
import { Claim, Debit, Submission } from 'interfaces';

import { DiffEdit } from 'deep-diff';

/**
 * Represents a change in a field that holds an object (one-to-one relation).
 */
export interface RelationFieldChange {
  type: 'relation';
  field: string;
  before: { id?: string; [key: string]: any } | null;
  after: { id?: string; [key: string]: any } | null;
  /** An array of difference objects (each with path, lhs, and rhs). */
  differences: DiffEdit<string, string>[];
  changedBy: string;
  updatedAt: string;
}

/**
 * Represents a change in a field that holds a primitive value.
 */
export interface PrimitiveFieldChange {
  type: 'primitive';
  /** The name of the field that was changed. */
  field: string;
  /** The value before the change. */
  before: string | number | null;
  /** The value after the change. */
  after: string | number | null;
  /** The ID of the user who made the change. */
  changedBy?: string;
  /** ISO timestamp when the change occurred. */
  updatedAt?: string;
}

/**
 * Represents changes in fields that store an array (many-to-many or one-to-many relations).
 */
export interface ArrayFieldChange {
  type: 'array';
  field: string;
  /** The full array before the change. */
  before: any[];
  /** The full array after the change. */
  after: any[];
  /** Items that were added. */
  added: any[];
  /** Items that were removed. */
  removed: any[];
  /**
   * For items that existed in both arrays but were modified,
   * each object contains the item's id and an array of differences.
   */
  modified: {
    id: string;
    differences: DiffEdit<string, string>[];
  }[];
  changedBy: string;
  updatedAt: string;
}

export type ClaimChange =
  | PrimitiveFieldChange
  | RelationFieldChange
  | ArrayFieldChange
  | ClaimHistoryActivity;

export type { ClaimHistoryActivity };

export interface ClaimHistory extends Claim {
  id: string;
  claimID: string;
  claim: Claim;
  changes: ClaimChange[];
  debits: Debit[];
  submissions: Submission[];
}

export const isClaimHistory = (obj: any): obj is ClaimHistory => {
  return claimHistoriesSchema.safeParse(obj).success;
};
